/* eslint-disable no-template-curly-in-string */
import crypto from 'crypto';
import { Button, Icon, Tag, Typography, SelectMenu } from '@scale/scaleui';
import { Spinner } from '@scale/scaleui-radix';
import React, { useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { useQuery } from 'react-query';
import { ChatTaskContext } from 'react-scale/components/scalers/tasks/ThreadedInteraction/contexts/ChatTaskContext';
import { StepCard } from 'react-scale/components/scalers/tasks/ThreadedInteraction/step/StepCard';
import apiFetch from 'react-scale/lib/api-fetch';
import { useChatStoreExtract } from 'react-scale/components/scalers/tasks/Chat/hooks/ChatStore.hooks';
import { RESET_OUTPUT_TOOL_ID } from '@scale/shared/chatTaskTools/types';
import {
  UIGenerationMetadata,
  UIGenerationLanguage,
  UIGenerationResult,
  UIGenerationEnforcementMode,
} from '@scale/shared/types/chat';
import { assertNever } from '@scale/shared/types/utils';
import { faRefresh, faCheck, faTriangleExclamation } from '@fortawesome/pro-regular-svg-icons';

function getUIGenerationLanguageString(language: UIGenerationLanguage) {
  switch (language) {
    case UIGenerationLanguage.React:
      return 'React';
    case UIGenerationLanguage.NextJS:
      return 'Next.js (code with "next/*" imports)';
    case UIGenerationLanguage.HTML:
      return 'HTML';
    case UIGenerationLanguage.Vue:
      return 'Vue';
    case UIGenerationLanguage.SolidJS:
      return 'SolidJS';
    case UIGenerationLanguage.Svelte:
      return 'Svelte';
    default:
      assertNever(language);
  }
}

// eslint-disable-next-line complexity
export const ChatHtmlRenderer = ({
  source,
  src,
  enforcementMode,
  setEnforcementPassed,
  contentUrlWithMetadata,
  setContentGeneratedUrlWithMetadata,
  uid,
}: {
  source: string;
  src: string;
  enforcementMode?: UIGenerationEnforcementMode;
  setEnforcementPassed?: (passed: boolean) => void;
  // Used to persist to response
  contentUrlWithMetadata?: {
    reactUIGeneratedUrl?: string;
  } & UIGenerationMetadata;
  setContentGeneratedUrlWithMetadata?: (
    urlWithMetadata: {
      reactUIGeneratedUrl?: string;
    } & UIGenerationMetadata,
  ) => void;
  uid: string;
}) => {
  const config = useChatStoreExtract(state => state.config);
  const [signedContentUrl, setSignedContentUrl] = useState<string | null>(
    contentUrlWithMetadata?.reactUIGeneratedUrl ?? null,
  );
  const [errorReason, setErrorReason] = useState<string | null>(
    contentUrlWithMetadata?.lastErrorMessage ?? null,
  );
  const [envType, setEnvType] = useState<UIGenerationLanguage | null>(
    contentUrlWithMetadata?.lastRunLanguage ?? null,
  );
  const [lastCodeRunHash, setLastCodeRunHash] = useState<string | null>(
    contentUrlWithMetadata?.lastCodeRunHash ?? null,
  );
  const [lastRunResult, setLastRunResult] = useState<UIGenerationResult | null>(
    contentUrlWithMetadata?.lastRunResult ?? null,
  );
  const [lastRunTimestamp, setLastRunTimestamp] = useState<number | null>(
    contentUrlWithMetadata?.lastUIGenerationTimestamp ?? null,
  );
  const taskContext = useContext(ChatTaskContext);

  const hashedSrc = useMemo(() => {
    return crypto.createHash('md5').update(src).digest('hex');
  }, [src]);

  useEffect(() => {
    if (enforcementMode === UIGenerationEnforcementMode.CompilationAttempted) {
      setEnforcementPassed?.(hashedSrc === lastCodeRunHash);
    } else if (enforcementMode === UIGenerationEnforcementMode.CompilationSuccessful) {
      setEnforcementPassed?.(
        hashedSrc === lastCodeRunHash && lastRunResult === UIGenerationResult.Success,
      );
    } else {
      setEnforcementPassed?.(true);
    }
  }, [enforcementMode, hashedSrc, lastCodeRunHash, lastRunResult, setEnforcementPassed]);

  const extractContent = useCallback(
    (regex: RegExp) => {
      const match = src.match(regex);
      return match ? match[1].trim() : '';
    },
    [src],
  );

  const htmlContent = useMemo(() => extractContent(/```html([\s\S]*?)```/), [extractContent]);
  const cssContent = useMemo(() => extractContent(/```css([\s\S]*?)```/), [extractContent]);
  const jsContent = useMemo(
    () => extractContent(/```(?:js|JAVASCRIPT|javascript|ts|typescript|TYPESCRIPT)([\s\S]*?)```/),
    [extractContent],
  );
  const reactTsContent = useMemo(() => extractContent(/```(?:.*)([\s\S]*?)```/), [extractContent]);

  const templateString = useMemo(() => {
    return `
      <!DOCTYPE html>
      <html>
        <head>
          <style>${cssContent}</style>
        </head>
        <body>
          ${htmlContent}
          <script type="text/javascript">
            (function() {
              ${jsContent}
            })();
          </script>
        </body>
      </html>
      `;
  }, [cssContent, htmlContent, jsContent]);

  const modifiedSrc = useMemo(() => {
    return config.codeRegex
      ? src.replace(new RegExp(config.codeRegex, 'g'), (match, capturedContent) => {
          return templateString.replace('${htmlContent}', capturedContent.trim());
        })
      : templateString;
  }, [config.codeRegex, src, templateString]);

  const {
    isFetching: isLoading,
    refetch: generateAndUpload,
    error,
  } = useQuery(
    ['ChatHtmlRenderer', modifiedSrc, taskContext.assignmentId, source, uid],
    async () => {
      // Then upload the output to CDS
      let cdsSrc;
      if (
        envType === 'react' ||
        envType === 'nextjs' ||
        envType === 'vue' ||
        envType === 'solidjs' ||
        envType === 'svelte'
      ) {
        const bundleOutput = await apiFetch(null, 'internal/genai/invokeChatTool', {
          method: 'POST',
          body: {
            toolId: 'react_to_html',
            params: {
              code: reactTsContent,
              language: envType,
            },
          },
        });
        cdsSrc = bundleOutput.html;
      } else {
        // Validate HTML before uploading
        if (htmlContent.trim() === '') {
          throw new Error('Invalid HTML: No content');
        }
        cdsSrc = modifiedSrc;
      }

      const cdsOutput = await apiFetch(null, 'internal/contentUnderstanding/uploadRawContent', {
        method: 'POST',
        body: {
          assignmentId: taskContext.assignmentId,
          content: cdsSrc,
          mimeType: 'text/html',
        },
      });

      return cdsOutput;
    },
    {
      enabled: false,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      refetchOnReconnect: false,
      retry: false,
      onSuccess(cdsOutput) {
        const lastCodeRunHash = crypto.createHash('md5').update(src).digest('hex');
        setLastCodeRunHash(lastCodeRunHash);
        setLastRunResult(UIGenerationResult.Success);
        setLastRunTimestamp(new Date().getTime());
        setContentGeneratedUrlWithMetadata?.({
          reactUIGeneratedUrl: cdsOutput.publicUrl,
          lastUIGenerationTimestamp: new Date().getTime(),
          lastRunLanguage: envType ?? undefined,
          lastRunResult: UIGenerationResult.Success,
          lastCodeRunHash,
        });
        setSignedContentUrl(cdsOutput.publicUrl);
        setErrorReason(null);
      },
      onError(err: Error) {
        const lastCodeRunHash = crypto.createHash('md5').update(src).digest('hex');
        setLastCodeRunHash(lastCodeRunHash);
        setLastRunTimestamp(new Date().getTime());
        let errorReason;
        if (
          (err as any)?.response?.message &&
          ((err as any)?.response?.message?.search(RESET_OUTPUT_TOOL_ID) ?? -1) !== -1
        ) {
          errorReason = (err as any).response.message.split(RESET_OUTPUT_TOOL_ID)[1]?.slice(0, 300);
        } else {
          errorReason = err.message;
        }
        setErrorReason(errorReason);
        setLastRunResult(UIGenerationResult.Failure);
        setContentGeneratedUrlWithMetadata?.({
          reactUIGeneratedUrl: undefined,
          lastUIGenerationTimestamp: new Date().getTime(),
          lastRunLanguage: envType ?? undefined,
          lastRunResult: UIGenerationResult.Failure,
          lastErrorMessage: errorReason,
          lastCodeRunHash,
        });
      },
    },
  );

  const codeGenStatus = useMemo(() => {
    if (hashedSrc === lastCodeRunHash) {
      // already run (fail or success)
      return lastRunResult === UIGenerationResult.Success ? (
        <div className="flex flex-row items-center gap-2">
          <Typography.Caption className="text-success-700">Render successful</Typography.Caption>
          <Icon icon={faCheck} variant="success" size="md" />
        </div>
      ) : (
        <div className="flex flex-row items-center">
          <Typography.Caption className="text-danger-700">Render failed</Typography.Caption>
          <Icon icon={faTriangleExclamation} variant="danger" size="md" />
        </div>
      );
    } else if (!lastRunResult) {
      // not run yet
      return null;
    } else {
      // code changed since run
      return (
        <div className="flex flex-row items-center">
          <Typography.Caption className="text-warning-700">
            Code changed since last render. Please render again
          </Typography.Caption>
          <Icon icon={faRefresh} variant="warning" size="md" />
        </div>
      );
    }
  }, [hashedSrc, lastCodeRunHash, lastRunResult]);

  return (
    <div className="flex flex-col my-2">
      <StepCard>
        <StepCard.Body>
          <div className="flex flex-col items-start gap-2 p-2">
            <Typography.Subtitle1>UI Generator</Typography.Subtitle1>
            {enforcementMode === UIGenerationEnforcementMode.CompilationAttempted &&
              hashedSrc !== lastCodeRunHash && (
                <Tag
                  className="whitespace-pre-line break-words"
                  variant="neutral"
                  icon="exclamation-circle"
                >
                  Required: You must attempt to render the code (regardless of success) in order to
                  proceed.
                </Tag>
              )}
            {enforcementMode === UIGenerationEnforcementMode.CompilationSuccessful &&
              (hashedSrc !== lastCodeRunHash || lastRunResult !== UIGenerationResult.Success) && (
                <Tag
                  className="whitespace-pre-line break-words"
                  variant="neutral"
                  icon="exclamation-circle"
                >
                  Required: The code must successfully compile and generate a UI link in order to
                  proceed.
                </Tag>
              )}
            <Typography.Overline3>Select UI Bundler</Typography.Overline3>
            <SelectMenu
              options={
                config.uiCodeGenerationLanguages && config.uiCodeGenerationLanguages.length > 0
                  ? config.uiCodeGenerationLanguages.map(language => ({
                      id: language,
                      name: getUIGenerationLanguageString(language),
                    }))
                  : Object.values(UIGenerationLanguage).map(language => ({
                      id: language,
                      name: getUIGenerationLanguageString(language),
                    }))
              }
              selectedOptionId={envType ?? ''}
              setSelected={value => setEnvType(value as UIGenerationLanguage)}
              disabled={isLoading}
              className="w-full"
            />
            {isLoading && (
              <div className="flex flex-row items-center gap-2">
                <Spinner size="3" />
                <div className="text-center text-gray-500">Generating UI and uploading...</div>
              </div>
            )}
            {!isLoading && codeGenStatus}
            <Button
              fullWidth
              size="lg"
              variant="primary"
              onClick={() => generateAndUpload()}
              disabled={isLoading || !envType}
            >
              Render UI
            </Button>
            {signedContentUrl && !isLoading && !error && (
              <Button
                className="border-none"
                fullWidth
                size="lg"
                variant="outline"
                icon={{
                  position: 'left',
                  type: ['fas', 'external-link'],
                  variant: 'outline',
                }}
                onClick={() => window.open(signedContentUrl, '_blank')}
              >
                View generated UI
              </Button>
            )}
            {error && (
              <div className="font-mono text-danger-700 bg-neutral-900 p-4 rounded">
                <p className="whitespace-pre-line break-words">{`Unable to generate and upload the bundled UI.\n\n${errorReason}`}</p>
              </div>
            )}
            {lastRunTimestamp && (
              <Typography.Caption className="text-neutral-500">
                Last rendered {new Date(lastRunTimestamp).toLocaleString()}
              </Typography.Caption>
            )}
          </div>
        </StepCard.Body>
      </StepCard>
    </div>
  );
};
